import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { reloadPage } from "./reload";
import { BUNDLE_VERSION, VERSION_QUERY_KEY, checkForNewVersion, fetchDeployedVersion } from "./version";

const button = "h-9 cursor-pointer rounded-pill bg-cta-bg px-4 text-[13px] font-bold text-cta-fg";

const browserFetch: typeof fetch = (...args) => fetch(...args);

/**
 * Says when a newer release is deployed than this tab runs, with **Reload**
 * (#290; ADR 0117). It never reloads by itself, so nothing typed is lost.
 * It checks when the window gets focus; the day view also checks with each
 * poll. A dev build, a check that fails, or no /version.json shows nothing,
 * and a check that fails later leaves the banner as it was.
 */
export function NewVersionBanner({
  bundleVersion = BUNDLE_VERSION,
  fetchFn = browserFetch,
  onReload = reloadPage,
}: {
  bundleVersion?: string | null;
  fetchFn?: typeof fetch;
  onReload?: () => void;
}) {
  const queryClient = useQueryClient();
  const checks = bundleVersion !== null;
  const deployed = useQuery({
    queryKey: VERSION_QUERY_KEY,
    queryFn: () => fetchDeployedVersion(fetchFn),
    enabled: checks,
    retry: false,
    // The window's focus event below covers this, and switching apps too.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!checks) {
      return;
    }
    const onFocus = () => {
      checkForNewVersion(queryClient);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [checks, queryClient]);

  if (!checks || deployed.data === undefined || deployed.data === bundleVersion) {
    return null;
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-panel border border-status-in-progress-line bg-status-in-progress-bg px-4 py-3 text-sm text-status-in-progress-fg"
    >
      <p className="min-w-0 flex-1">A new version is available.</p>
      <button type="button" className={button} onClick={onReload}>
        Reload
      </button>
    </div>
  );
}
